export interface HighlightTextConfig {
  highlightTexts: Array<{
    text: string;
    isPrimary?: boolean;
    pages?: number[];
  }>;
  scrollToPrimaryText: (fallbackElement?: HTMLElement) => void;
}

export interface DesiredItem {
  startIdx: number;
  endIndex: number;
  node: Text;
  nodeValue: string;
}

const normalizeText = (text: string) => {
  return text
    .toLowerCase()
    .replaceAll('⁄', '/')
    .replaceAll('’', "'")
    .replaceAll('‘', "'")
    .replaceAll('–', '--')
    .replace(/\s+/g, '');
};

export const highlightText = (desired: Array<DesiredItem>) => {
  desired.forEach(item => {
    if (item.node.parentNode) {
      const nodeContent = item.nodeValue;
      const before = nodeContent.slice(0, item.startIdx);
      const match = nodeContent.slice(item.startIdx, item.endIndex);
      const after = nodeContent.slice(item.endIndex);

      const mark = document.createElement('mark');
      mark.classList.add('primary');
      mark.textContent = match;

      const fragment = document.createDocumentFragment();

      if (before) {
        fragment.appendChild(document.createTextNode(before));
      }

      fragment.appendChild(mark);

      if (after) {
        fragment.appendChild(document.createTextNode(after));
      }

      item.node.parentNode.replaceChild(fragment, item.node);
    }
  });
};

/**
 * step by step logic:
 * 1. get all pdfjs text layer spans
 * 2. loop through all text chars and map to corresponding span
 * 3. find citation index in normalized text
 * 4. get corresponding span index via indexToSpan
 * 5. highlight and scroll into view
 * @param container - [HTMLElement]
 * @param searchText - [String]
 */
export const highlightCitationInPDF = ({
  container,
  searchText,
}: {
  container: HTMLElement;
  searchText: string;
}) => {
  const spans = Array.from(
    container.querySelectorAll('span[role="presentation"]')
  );
  let fullText = '';
  const indexToSpan: number[] = [];
  spans.forEach((span, spanIdx) => {
    if (span.textContent) {
      const text = normalizeText(span.textContent);
      [...text].forEach(text => {
        fullText += text;
        indexToSpan.push(spanIdx);
      });
    }
  });
  const normalizedSearch = normalizeText(searchText);
  const matchIdx = fullText.indexOf(normalizedSearch);

  if (matchIdx === -1) {
    return;
  }

  const highlightSpanIds = new Set<number>();

  for (let i = matchIdx; i < matchIdx + normalizedSearch.length; i++) {
    if (indexToSpan[i]) {
      highlightSpanIds.add(indexToSpan[i]);
    }
  }

  highlightSpanIds.forEach(id => {
    const mark = document.createElement('mark');
    mark.classList.add('primary');
    mark.textContent = spans[id].textContent;
    spans[id].textContent = '';
    spans[id].appendChild(mark);
  });

  spans[Math.min(...highlightSpanIds)].scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
};

export const highlightElement = ({
  part,
  container,
  shouldAddSpace,
  replaceNodeValue,
  filter,
}: {
  part: string;
  container: Node;
  shouldAddSpace: (
    content: string,
    currentNode: Node,
    prevNode?: Node
  ) => boolean;
  replaceNodeValue: (value: string) => string;
  filter?: NodeFilter;
}) => {
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    filter
  );

  let content = '';
  let done = false;
  const nodes: Text[] = [];
  const desired: Array<DesiredItem> = [];

  while (walker.nextNode() && !done) {
    const node = walker.currentNode as Text;
    nodes.push(node);

    if (shouldAddSpace(content, node, nodes[nodes.length - 2])) {
      content += ' ';
    }

    let replaced = replaceNodeValue(node.nodeValue!);
    content +=
      content.endsWith(' ') && replaced.startsWith(' ')
        ? replaced.trimStart()
        : replaced;

    if (content.indexOf(part) !== -1) {
      // this is needed to find the index in the replaced string
      // example: part is "this is a random letter from anything else" and replaced is "this is a random letter"
      while (part.indexOf(replaced) === -1) {
        replaced = replaced.slice(0, -1);
      }

      let startIdx = 0;
      let i = 1;
      let remPart = part;

      desired.unshift({
        node,
        startIdx: 0,
        endIndex: replaced.length,
        nodeValue: replaced,
      });
      content = content.slice(0, -replaced.length);
      remPart = remPart.slice(0, -replaced.length);
      let diff = remPart.length;

      while (content.indexOf(remPart) !== -1 && remPart.length) {
        const node = nodes[nodes.length - 1 - i];
        // handle many nbsp at the end
        let repl = replaceNodeValue(node.nodeValue!);
        const minusSpace = Number(shouldAddSpace(repl, node, desired[0].node));

        startIdx =
          repl.length <= remPart.length
            ? 0
            : repl.length - remPart.length + minusSpace;
        const sliceIdx = -Math.min(repl.length, diff) - minusSpace;
        content = content.slice(0, sliceIdx);
        remPart = remPart.slice(0, sliceIdx);
        desired.unshift({
          node,
          startIdx,
          endIndex: repl.length,
          nodeValue: repl,
        });
        diff += -repl.length - minusSpace;
        i++;
      }

      highlightText(desired);
      done = true;
    }
  }
};
